import React from 'react'

import { Button, Dropdown, Tooltip } from 'antd'
import type { MenuProps } from 'antd'

import i18n from 'i18n'

import dataManager from 'pages/main/data/DataManager'

import classNames from 'classnames'

import { useLargeAttachment, useReadReceipt, useScheduledEmail } from 'vars'

import toolbarSend from 'assets/images/toolbar-send.svg'
import toolbarScheduleSend from 'assets/images/toolbar-schedule-send.svg'
import toolbarAttachment from 'assets/images/toolbar-attachment.svg'
import toolbarSignature from 'assets/images/toolbar-signature.svg'
import toolbarDelete from 'assets/images/toolbar-delete.svg'
import toolbarProposeFreeTime from 'assets/images/toolbar-propose-free-time.svg'
import toolbarMore from 'assets/images/toolbar-more-h.svg'
import toolbarSecureSend from 'assets/images/toolbar-secure-send.svg'
import toolbarReadReceiptOn from 'assets/images/toolbar-read-receipt-on.svg'
import toolbarReadReceiptOff from 'assets/images/toolbar-read-receipt-off.svg'

import { SVGImageCurrentColor } from 'utils/svgHelper'
import type { ExpirationKey } from 'utils/ui/SecureSend'

import chevronDownThick16 from 'assets/images/chevron-down-thick-16.svg'

import styles from './style.module.scss'

const tooltipZIndex = 1950

interface ReactObj {
  state: {
    sendDisabled: boolean
    secureSendEnabled: boolean
    secureSendExpirationKey: ExpirationKey | null
    readReceiptEnabled: boolean
  }
  onToolbarClick: (action: string, data?: any) => void
}

function getSendElem(reactObj: ReactObj) {
  const { sendDisabled } = reactObj.state

  return (
    <Tooltip title={i18n.t('editor.tooltip.send')} zIndex={tooltipZIndex}>
      <Button
        type="text"
        icon={<img src={toolbarSend} alt="send" />}
        data-testid="mail-send-button"
        disabled={sendDisabled}
        onClick={() => {
          reactObj.onToolbarClick('send')
        }}
      />
    </Tooltip>
  )
}

function getScheduleSendElem(reactObj: ReactObj) {
  const { sendDisabled } = reactObj.state

  return (
    <Tooltip title={i18n.t('editor.tooltip.scheduleSend')} zIndex={tooltipZIndex}>
      <Button
        type="text"
        icon={<img src={toolbarScheduleSend} alt="schedule send" />}
        disabled={sendDisabled}
        onClick={() => {
          reactObj.onToolbarClick('scheduleSend')
        }}
      />
    </Tooltip>
  )
}

function getAttachmentElem(reactObj: ReactObj) {
  if (useLargeAttachment !== true) {
    return (
      <Tooltip title={i18n.t('editor.tooltip.attachment')} zIndex={tooltipZIndex}>
        <Button
          type="text"
          icon={<img src={toolbarAttachment} alt="attachment" />}
          onClick={() => {
            reactObj.onToolbarClick('attachment', 'local')
          }}
        />
      </Tooltip>
    )
  }

  const menuAttrs: MenuProps = {
    items: [
      {
        label: i18n.t('editor.attachment.normal'),
        key: 'local',
      },
      {
        label: i18n.t('editor.attachment.share'),
        key: 'share',
      },
    ],
    onClick: ({ key }) => {
      reactObj.onToolbarClick('attachment', key)
    },
  }

  const dropdownAttrs = {
    overlayClassName: classNames('contextMenu'),
    menu: menuAttrs,
    trigger: ['click' as const],
  }

  return (
    <Tooltip
      title={i18n.t('editor.tooltip.attachment')}
      zIndex={tooltipZIndex}
      trigger={['hover', 'click']}
    >
      <Dropdown {...dropdownAttrs}>
        <span className={classNames(styles.attachmentElem)}>
          <Button type="text" icon={<img src={toolbarAttachment} alt="attachment" />} />
          <img src={chevronDownThick16} alt="chevron" style={{ marginLeft: '4px' }} />
        </span>
      </Dropdown>
    </Tooltip>
  )
}

function getSignatureElem(reactObj: ReactObj) {
  const menuItems = dataManager.getSignatureList().map((item: any) => {
    const { signatureId, title, titleKey } = item
    let label = title
    if (titleKey != null) {
      /* i18next-extract-disable-next-line */
      label = i18n.t(titleKey)
    }
    return {
      label,
      key: signatureId,
    }
  })

  const menuAttrs: MenuProps = {
    items: [
      {
        label: i18n.t('editor.signature.none'),
        key: 'none',
      },
      ...menuItems,
    ],
    onClick: ({ key }) => {
      reactObj.onToolbarClick('signature', key)
    },
  }

  const dropdownAttrs = {
    overlayClassName: classNames('contextMenu', styles.signatureMenu),
    menu: menuAttrs,
    trigger: ['click' as const],
  }

  return (
    <Tooltip
      title={i18n.t('editor.tooltip.signature')}
      zIndex={tooltipZIndex}
      trigger={['hover', 'click']}
    >
      <Dropdown {...dropdownAttrs}>
        <span className={classNames(styles.signatureWrapper)}>
          <Button type="text" icon={<img src={toolbarSignature} alt="signature" />} />
          <img src={chevronDownThick16} alt="chevron" style={{ marginLeft: '4px' }} />
        </span>
      </Dropdown>
    </Tooltip>
  )
}

function getDeleteElem(reactObj: ReactObj) {
  return (
    <Tooltip title={i18n.t('editor.tooltip.delete')} zIndex={tooltipZIndex}>
      <Button
        type="text"
        icon={<img src={toolbarDelete} alt="delete" />}
        onClick={() => {
          reactObj.onToolbarClick('delete')
        }}
      />
    </Tooltip>
  )
}

function getSecureSendElem(reactObj: ReactObj) {
  const { secureSendEnabled } = reactObj.state
  const iconColor = secureSendEnabled ? '#FD4C45' : '#000000'

  return (
    <Tooltip title={i18n.t('editor.tooltip.secureSend')} zIndex={tooltipZIndex}>
      <Button
        type="text"
        icon={<SVGImageCurrentColor src={toolbarSecureSend} alt="secure send" />}
        style={{ color: iconColor }}
        onClick={() => {
          reactObj.onToolbarClick('secureSend', { enabled: secureSendEnabled })
        }}
      />
    </Tooltip>
  )
}

function getReadReceiptElem(reactObj: ReactObj) {
  const { readReceiptEnabled } = reactObj.state
  // ON/OFF 상태에 따라 다른 SVG (filled vs outline) — 모양이 달라 색상만으로 표현 불가.
  const iconSrc = readReceiptEnabled ? toolbarReadReceiptOn : toolbarReadReceiptOff
  const iconColor = readReceiptEnabled ? '#0077FF' : '#000000'

  return (
    <Tooltip title={i18n.t('editor.tooltip.readReceipt')} zIndex={tooltipZIndex}>
      <Button
        type="text"
        // key={iconSrc}: src가 바뀌어도 SVGImage가 cache hit이면 setSvgElem 을 호출하지 않아
        // 이전 SVG element가 그대로 남는 버그가 있다. key 로 강제 재마운트하여 회피.
        icon={<SVGImageCurrentColor key={iconSrc} src={iconSrc} alt="read receipt" />}
        style={{ color: iconColor }}
        onClick={() => {
          reactObj.onToolbarClick('readReceipt', { enabled: readReceiptEnabled })
        }}
      />
    </Tooltip>
  )
}

function getProposeFreeTimeElem(reactObj: ReactObj) {
  return (
    <Tooltip title={i18n.t('editor.tooltip.proposeFreeTime')} zIndex={tooltipZIndex}>
      <Button
        type="text"
        icon={<img src={toolbarProposeFreeTime} alt="propose free time" />}
        onClick={() => {
          reactObj.onToolbarClick('proposeFreeTime')
        }}
      />
    </Tooltip>
  )
}

function getMoreElem(reactObj: ReactObj) {
  const { sendDisabled } = reactObj.state

  const menuAttrs: MenuProps = {
    items: [
      {
        label: i18n.t('editor.etc.saveDraft'),
        disabled: sendDisabled,
        key: 'saveDraft',
      },
    ],
    onClick: ({ key }) => {
      reactObj.onToolbarClick('more', key)
    },
  }

  if (__ENV_LOCAL__) {
    menuAttrs.items?.push({
      label: i18n.t('editor.etc.saveToLocal'),
      key: 'saveLocal',
    })
  }

  const dropdownAttrs = {
    overlayClassName: classNames('contextMenu'),
    menu: menuAttrs,
    trigger: ['click' as const],
  }

  return (
    <Tooltip title={i18n.t('editor.tooltip.etc')} zIndex={tooltipZIndex}>
      <Dropdown {...dropdownAttrs}>
        <Button type="text" icon={<img src={toolbarMore} alt="more" />} />
      </Dropdown>
    </Tooltip>
  )
}

function getToolbarElem(reactObj: ReactObj) {
  let scheduleSendElem = null
  if (useScheduledEmail === true) {
    scheduleSendElem = (
      <React.Fragment>
        {getScheduleSendElem(reactObj)}
        <div className={classNames(styles.empty)} />
      </React.Fragment>
    )
  }

  return (
    <div className={classNames(styles.toolbar, 'toolbar')}>
      {getSendElem(reactObj)}
      {scheduleSendElem}
      {getAttachmentElem(reactObj)}
      <div className={classNames(styles.empty)} />
      {getSignatureElem(reactObj)}
      <div className={classNames(styles.empty)} />
      {getSecureSendElem(reactObj)}
      {useReadReceipt && getReadReceiptElem(reactObj)}
      {getProposeFreeTimeElem(reactObj)}
      {getDeleteElem(reactObj)}
      {getMoreElem(reactObj)}
      <div className={classNames(styles.empty)} />
    </div>
  )
}

const ext = {
  getToolbarElem,
}

export default ext
